import type { CountryCode, ThumbnailFormatEnum } from '@/graphql/saleor/api';

import type { Brand } from './types';

const normalizeApiBaseUrl = (url: string): string => {
  if (!url) return '';

  const trimmed = url.trim();
  const lowerTrimmed = trimmed.toLowerCase();
  if (
    !(lowerTrimmed.startsWith('http://') || lowerTrimmed.startsWith('https://'))
  ) {
    return '';
  }

  const [protocol, ...restParts] = trimmed.split('://');
  const rest = restParts.join('://');

  let normalizedRest = '';
  let previousWasSlash = false;
  for (const char of rest) {
    if (char === '/') {
      if (!previousWasSlash) {
        normalizedRest += char;
      }
      previousWasSlash = true;
    } else {
      normalizedRest += char;
      previousWasSlash = false;
    }
  }
  while (normalizedRest.endsWith('/')) {
    normalizedRest = normalizedRest.slice(0, -1);
  }

  return `${protocol.toLowerCase()}://${normalizedRest}/`;
};

export const VERSION = `${process.env.APP_NAME}@${process.env.APP_VERSION}`;
export const QR_CODE_KEY = 'qrCode';
export const DEFAULT_LOCALE = 'default';
export const NEXT_PUBLIC_DEFAULT_APP_LOCALE =
  process.env.NEXT_PUBLIC_DEFAULT_APP_LOCALE!;

export const STOREFRONT_NAME = process.env.NEXT_PUBLIC_STOREFRONT_NAME || '';

export const PUBLIC_URL = process.env.NEXT_PUBLIC_URL!;

export const IS_TEST_ENV = process.env.NODE_ENV === 'test';

export const DETAIL_PAGE_IMAGE_SIZE = 1024;
export const DETAIL_PAGE_LARGE_IMAGE_SIZE = 2048;
export const CARDS_IMAGE_SIZE = 1024;
export const THUMBNAIL_IMAGE_SIZE = 512;
export const DEBOUNCE_TIME_IN_MS = 300;
export const DEBOUNCE_FILTER_TIME_IN_MS = 700;
// Inactivity before idle-gated UI may appear. Tunable per environment; 0 shows it
// at once. Parsed explicitly because 0 is falsy and || would swallow it.
const parsedIdleTimeout = Number(process.env.NEXT_PUBLIC_IDLE_TIMEOUT_IN_MS);
export const IDLE_TIMEOUT_IN_MS = Number.isFinite(parsedIdleTimeout)
  ? parsedIdleTimeout
  : 0;
export const ADD_TO_BAG_DIALOG_TIMEOUT = 3500;
export const STATICIMAGES_PATH_PREFIX = '/_next/static/images/';
export const NEXT_PUBLIC_IMAGES_FORMAT = 'WEBP' as ThumbnailFormatEnum;
export const STATIC_PATHS_FETCH_LIMIT = parseInt(
  process.env.STATIC_PATHS_FETCH_LIMIT!,
  10,
);
export const STATIC_PATHS_FALLBACK: boolean | 'blocking' = process.env
  .STATIC_PATHS_FALLBACK
  ? (() => {
      try {
        return JSON.parse(process.env.STATIC_PATHS_FALLBACK);
      } catch {
        return 'blocking';
      }
    })()
  : 'blocking';

export const API_URI = process.env.NEXT_PUBLIC_API_URI!;
export const ORDER_API_URI = process.env.NEXT_PUBLIC_ORDER_APP_API_URI!;

export const INCREMENTAL_STATIC_REGENERATION_REVALIDATE = parseInt(
  process.env.INCREMENTAL_STATIC_REGENERATION_REVALIDATE_STATIC_PAGE ?? '10',
  10,
);

export const GEOLOCATION = process.env.NEXT_PUBLIC_GEOLOCATION === 'true';

export const GRAPHQL_OPERATION_PREFIX =
  process.env.NEXT_PUBLIC_GRAPHQL_OPERATION_PREFIX || 'Storefront';

export const ORDER_APP_API_URI =
  process.env.NEXT_PUBLIC_ORDER_APP_API_URI || '';

export const ORDER_APP_REST_API_URI =
  process.env.NEXT_PUBLIC_ORDER_APP_REST_API_URI || '';

export const CONFIGURATION_APP_API_URI =
  process.env.NEXT_PUBLIC_CONFIGURATION_APP_API_URI || '';
export const POS_MANAGER_APP_API_URI =
  process.env.NEXT_PUBLIC_POS_MANAGER_APP_API_URI || '';
export const POS_MANAGER_APP_REST_API_URI =
  process.env.NEXT_PUBLIC_POS_MANAGER_APP_REST_API_URI || '';
export const ADYEN_API_URI = process.env.NEXT_PUBLIC_ADYEN_APP_API_URI || '';

export const MY_ACCOUNT_APP_API_URI = normalizeApiBaseUrl(
  process.env.NEXT_PUBLIC_MY_ACCOUNT_APP_API_URI || '',
);

export const SALEOR_APP_TOKEN = process.env.SALEOR_APP_TOKEN!;

export const CONTENTFUL_SPACE_ID = process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID!;
export const CONTENTFUL_ACCESS_TOKEN =
  process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN!;
export const CONTENTFUL_ENVIRONMENT =
  process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT!;
export const CONTENTFUL_URI = `https://graphql.contentful.com/content/v1/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`;
export const CONTENTFUL_DELIVERY_ACCESS_TOKEN =
  process.env.NEXT_PUBLIC_CONTENTFUL_DELIVERY_ACCESS_TOKEN || '';

export const ECS_API_URI = process.env.ECS_API_URI!;
export const ECS_ACCESS_USERNAME = process.env.ECS_ACCESS_USERNAME!;
export const ECS_ACCESS_PASSWORD = process.env.ECS_ACCESS_PASSWORD!;

export const ECS_APP_API_URI = process.env.ECS_APP_API_URI!;

export const ENVIRONMENT_NAME = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME!;
export const SMBC_SIMULATION_URL =
  process.env.NEXT_PUBLIC_SMBC_SIMULATION_URL || '';

// Global-e
export const GLOBAL_E_MERCHANT_ID =
  process.env.NEXT_PUBLIC_GLOBAL_E_MERCHANT_ID!;
export const GLOBAL_E_MERCHANT_GUID =
  process.env.NEXT_PUBLIC_GLOBAL_E_MERCHANT_GUID!;
export const GLOBAL_E_URL = process.env.NEXT_PUBLIC_GLOBAL_E_URL!;
export const GLOBAL_E_MARKET_AVAILABLE =
  process.env.NEXT_PUBLIC_GLOBAL_E_MARKET_AVAILABLE!.split(',');

// Algolia
export const ALGOLIA_APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!;
export const ALGOLIA_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY!;
export const ALGOLIA_SEARCH_API_KEY =
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY!;
export const ALGOLIA_PREVIEW_API_KEY =
  process.env.NEXT_PUBLIC_ALGOLIA_PREVIEW_API_KEY!;
export const ALGOLIA_SSR_API_KEY = process.env.ALGOLIA_SSR_API_KEY || '';
export const ALGOLIA_PREVIEW_SSR_API_KEY =
  process.env.ALGOLIA_PREVIEW_SSR_API_KEY || '';

export const WIDGET_ALGOLIA_API_KEY =
  process.env.NEXT_PUBLIC_WIDGET_ALGOLIA_API_KEY || '';

export const ADYEN_MERCHANT_ACCOUNT_US =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_US || '';
export const ADYEN_MERCHANT_ACCOUNT_CA =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_CA || '';
export const ADYEN_MERCHANT_ACCOUNT_CH =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_CH || '';
export const ADYEN_MERCHANT_ACCOUNT_UK =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_UK || '';
export const ADYEN_MERCHANT_ACCOUNT_EU =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_EU || '';
export const ADYEN_MERCHANT_ACCOUNT_JP =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_JP || '';
export const ADYEN_MERCHANT_ACCOUNT_AU =
  process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_AU || '';
export const ADYEN_EU_ACCOUNTS =
  process.env.NEXT_PUBLIC_ADYEN_EU_ACCOUNTS?.split(',') || [];
export const ADYEN_ENV = process.env.NEXT_PUBLIC_ADYEN_ENV || '';
export const ADYEN_CLIENT_KEY = process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY || '';
export const SERVICE_ADYEN_CLIENT_KEY =
  process.env.NEXT_PUBLIC_SERVICE_ADYEN_CLIENT_KEY || '';

export const UGC_API_KEY = process.env.UGC_API_KEY!;
export const UGC_PUBLIC_API_KEY = process.env.UGC_PUBLIC_API_KEY!;

export const MAILER_APP_API_URI =
  process.env.NEXT_PUBLIC_MAILER_APP_API_URI || '';
export enum BREITLING_PAYMENTS {
  ADYEN = 'brtlg.payments.adyen.v1',
}

export const SSR_MODE = typeof window === 'undefined';
export const IS_BROWSER = !SSR_MODE;
export const ALGOLIA_OFFLINE_MODE =
  process.env.NEXT_PUBLIC_ALGOLIA_OFFLINE_MODE === 'true';

export const AI_RECOMMENDATION_ENABLED =
  process.env.NEXT_PUBLIC_AI_RECOMMENDATION_TOGGLE === 'true';

const AI_RECO_THRESHOLD_RAW =
  process.env.NEXT_PUBLIC_AI_RECOMMENDATION_THRESHOLD?.trim() ?? '';

export const AI_RECOMMENDATION_THRESHOLD: number =
  AI_RECO_THRESHOLD_RAW !== '' && !Number.isNaN(Number(AI_RECO_THRESHOLD_RAW))
    ? Number(AI_RECO_THRESHOLD_RAW)
    : 80;

export const AI_RECOMMENDATION_MAX_ITEMS = 10;

export type AppEvents = 'searchOpen' | 'signatureExpired' | 'unhandledError';

export const CAROUSEL_FETCH_LIMIT = 10;

export const TEXT_MAX_LENGTH = 250;
export const ATTRIBUTES_SLUG = {
  wbrMaterialMktg: 'wbr-material-mktg',
};

export const CONSUMPTION_TAX_INCLUDED_COUNTRIES: CountryCode[] = ['JP'];
export const TAX_EXCLUDED_COUNTRIES: CountryCode[] = ['US', 'CA'];
export const VAT_INCLUDED_COUNTRIES: CountryCode[] = [
  'ZA',
  'BG',
  'SI',
  'SK',
  'HR',
  'RO',
  'CZ',
  'PL',
  'HU',
  'BH',
  'JO',
  'SA',
  'AE',
  'TH',
  'MY',
  'TW',
  'SG',
  'AT',
  'BE',
  'DK',
  'FI',
  'FR',
  'DE',
  'IT',
  'GR',
  'LU',
  'NO',
  'PT',
  'NL',
  'ES',
  'SE',
  'CH',
  'GB',
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AW',
  'AZ',
  'BS',
  'BB',
  'BZ',
  'BM',
  'BY',
  'BA',
  'BR',
  'VG',
  'BN',
  'KH',
  'KY',
  'CL',
  'CO',
  'DM',
  'CR',
  'CW',
  'CY',
  'DO',
  'EG',
  'EE',
  'FJ',
  'GE',
  'GH',
  'GI',
  'GT',
  'HN',
  'IS',
  'IN',
  'ID',
  'IQ',
  'IR',
  'IL',
  'CI',
  'JM',
  'KZ',
  'KE',
  // Saleor `CountryCode` does not include "XK", see https://docs.saleor.io/api-reference/miscellaneous/enums/country-code
  // 'XK',
  'KG',
  'LA',
  'LV',
  'LB',
  'LI',
  'LT',
  'MO',
  'MK',
  'MG',
  'MV',
  'MT',
  'MP',
  'MU',
  'MX',
  'MD',
  'MC',
  'ME',
  'MA',
  'MM',
  'NA',
  'NP',
  'NC',
  'NZ',
  'NG',
  'OM',
  'PK',
  'PA',
  'PY',
  'PE',
  'PH',
  'PR',
  'CG',
  'IE',
  'RE',
  'RU',
  'BL',
  'KN',
  'LC',
  'RS',
  'SC',
  'KR',
  'LK',
  'SX',
  'MF',
  'TJ',
  'TN',
  'TR',
  'TM',
  'TC',
  'UA',
  'UY',
  'VI',
  'UZ',
  'VE',
  'VN',
];
export const GST_INCLUDED_COUNTRIES: CountryCode[] = ['AU'];
export const NO_TAX_COUNTRIES: CountryCode[] = ['HK', 'KW', 'QA'];
export const IMPERIAL_COUNTRIES: CountryCode[] = ['US'];

export const UK_LABEL = {
  en: 'excluding Ireland/Jersey/Guernsey and Isle of Man',
  zh: '不包括爱尔兰/泽西岛/根西岛和马恩岛',
};

export enum CacheKey {
  CheckoutId = 'checkoutId',
  OrderId = 'orderId',
  IsChangedAfterOrderCreated = 'isChangedAfterOrderCreated',
}

export const STRAP_SIZES_FETCH_LIMIT = 10;
export const FILTERS_FETCH_LIMIT = 30;

export const MELISSA_SUGGESTION_API_URI =
  process.env.MELISSA_SUGGESTION_API_URI || '';
export const MELISSA_VALIDATE_API_URI =
  process.env.MELISSA_VALIDATE_API_URI || '';
export const MELISSA_API_KEY = process.env.MELISSA_API_KEY || '';

export const PAYMENT_APPS_ID =
  process.env.NEXT_PUBLIC_PAYMENT_APPS_ID?.split(',') ?? [];

export const HITS_PER_PAGE = 20;
export const HITS_COLLECTION_PER_PAGE = 20;

export const GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

export const GOOGLE_PAY_ENV = process.env.NEXT_PUBLIC_GOOGLEPAY_ENV || 'TEST';
export const GOOGLE_PAY_MERCHANT_ID =
  process.env.NEXT_PUBLIC_GOOGLEPAY_MERCHANT_ID || '';
export const GOOGLE_PAY_MERCHANT_NAME =
  process.env.NEXT_PUBLIC_GOOGLEPAY_MERCHANT_NAME || '';

export const SALESFORCE_URL = process.env.NEXT_PUBLIC_SALESFORCE_URL;

export const PERSONAL_AIDE_USE_MOCK =
  process.env.NEXT_PUBLIC_PERSONAL_AIDE_USE_MOCK === 'true';

export const PERSONAL_AIDE_USE_PRODUCTION_SALEOR =
  process.env.NEXT_PUBLIC_PERSONAL_AIDE_USE_PRODUCTION_SALEOR !== 'false';

export const PERSONAL_AIDE_COMPARISON_ENABLED =
  process.env.NEXT_PUBLIC_PERSONAL_AIDE_COMPARISON_ENABLED !== 'false';

export const PERSONAL_AIDE_CLASSIC_DISPLAY_ENABLED = false;

export const PERSONAL_AIDE_API_URL =
  process.env.NEXT_PUBLIC_PERSONAL_AIDE_API_URL ?? '';

export const PERSONAL_AIDE_PRODUCTS_API_URL = '/api/personal-aide/products/';

export const PERSONAL_AIDE_SALEOR_PRODUCTS_API_URL =
  '/api/personal-aide/saleor-products/';

export const PERSONAL_AIDE_PRODUCTION_DOMAIN = 'https://www.breitling.com';

export const PERSONAL_AIDE_PDP_LOCALE = 'us-en';

export const PERSONAL_AIDE_MAX_QUERY_LENGTH = 500;

export const PERSONAL_AIDE_SESSION_TTL_DAYS = 3;

export const WITHDRAWAL_ENABLED =
  process.env.NEXT_PUBLIC_WITHDRAWAL_ENABLED === 'true';

export const ASSETS_APP_API_URI = process.env.ASSETS_APP_API_URI || '';

export const IFRAME_RESIZER_LICENSE =
  process.env.NEXT_PUBLIC_IFRAME_RESIZER_LICENSE || '';

// Live Story (https://www.livestory.io) embed configuration.
// The brand-specific loader script defines `window.LiveStory`.
export const LIVESTORY_BRAND_CODE =
  process.env.NEXT_PUBLIC_LIVESTORY_BRAND_CODE || 'breitling';
export const LIVESTORY_SCRIPT_URL =
  process.env.NEXT_PUBLIC_LIVESTORY_SCRIPT_URL ||
  `https://assets.livestory.io/dist/livestory-${LIVESTORY_BRAND_CODE}.min.js`;
// The Live Story loader requires jQuery (it aliases `window.jQuery` to `$ls`
// and does not load jQuery itself), so it must be present before the loader runs.
export const LIVESTORY_JQUERY_URL =
  process.env.NEXT_PUBLIC_LIVESTORY_JQUERY_URL ||
  'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.6.0/jquery.min.js';
// Optional fixed Live Story store code. When empty, the page locale slug is used.
export const LIVESTORY_STORE = process.env.NEXT_PUBLIC_LIVESTORY_STORE || '';

export const EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION =
  process.env.NEXT_PUBLIC_EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION?.split(
    ',',
  ) || [];

export const BREITLING_ADDRESS = {
  company: 'Breitling SA',
  street: 'Léon breitling-strasse 2',
  city: '2540 Grenchen - Switzerland',
};

export const CONTACT_DETAILS: Partial<
  Record<
    CountryCode | 'DEFAULT',
    { contact_email: string; contact_fax?: string; contact_phone: string }
  >
> = {
  CA: {
    contact_phone: '+1 647-691-1073',
    contact_email: 'INFO.CA@BREITLING.COM',
  },
  US: {
    contact_phone: '+1 203-456-2458',
    contact_email: 'INFO.US@BREITLING.COM',
  },
  DEFAULT: {
    contact_phone: '+41 32 654 54 54',
    contact_email: 'contact@breitling.com',
    contact_fax: '+41 32 654 54 00',
  },
};

export const getContactDetailsFallbackInfo = () => ({
  email: CONTACT_DETAILS.DEFAULT?.contact_email ?? 'contact@breitling.com',
  phoneNumber: CONTACT_DETAILS.DEFAULT?.contact_phone ?? '+41 32 654 54 54',
  weekdayHours: 'Mon-Fri: 09:00-18:00',
  weekendHours: 'Sat - Sun: closed',
});

export const LOGIN_FAIL_MAX_COUNT = 5;

export const weekdays = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];
export const GOOGLEMAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLEMAPS_API_KEY;

export const closeSVGString = `<svg aria-hidden="true" class="pswp__icn"
xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
<rect id="Rectangle_24561" data-name="Rectangle 24561" width="24" height="24" fill="none"/>
<g id="Group_9492" data-name="Group 9492" transform="translate(2.002 2.002)">
  <path id="Line_2179" data-name="Line 2179" d="M19.019,19.641-.354.269.269-.354,19.641,19.019Z" transform="translate(0.354 0.354)" fill="#000000"/>
  <path id="Line_2180" data-name="Line 2180" d="M.269,19.641l-.623-.623L19.019-.354l.623.623Z" transform="translate(0.354 0.354)" fill="#000000"/>
</g>
</svg>`;

export const zoomSVGString = `<svg fill="#000000"  aria-hidden="true" class="pswp__icn" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
	 viewBox="0 0 52.966 52.966" xml:space="preserve">
<g>
	<path d="M28.983,20h-6v-6c0-0.552-0.448-1-1-1s-1,0.448-1,1v6h-6c-0.552,0-1,0.448-1,1s0.448,1,1,1h6v6c0,0.552,0.448,1,1,1
		s1-0.448,1-1v-6h6c0.552,0,1-0.448,1-1S29.535,20,28.983,20z" fill="#000000"/>
	<path d="M51.704,51.273L36.845,35.82c3.79-3.801,6.138-9.041,6.138-14.82c0-11.58-9.42-21-21-21s-21,9.42-21,21s9.42,21,21,21
		c5.083,0,9.748-1.817,13.384-4.832l14.895,15.491c0.196,0.205,0.458,0.307,0.721,0.307c0.25,0,0.499-0.093,0.693-0.279
		C52.074,52.304,52.086,51.671,51.704,51.273z M2.983,21c0-10.477,8.523-19,19-19s19,8.523,19,19s-8.523,19-19,19
		S2.983,31.477,2.983,21z" fill="#000000"/>
</g>
</svg>`;
export enum SALEOR_PRE_ORDER_CONFIG_KEY {
  NotifyMe = 'notify_me',
  Market = 'market',
}

export const storageInitSetting = {
  ignoreExceptions: false,
  // -- Cookie Store --
  cookie: {
    prefix: '',
    options: {
      path: '/',
    },
  },
  // -- localStorage Store --

  localStorage: {
    prefix: 'Breitling.',
  },
  // -- sessionStorage Store --

  sessionStorage: {
    prefix: 'Breitling.',
  },
  initialState: [],
};

export enum LOCAL_STORAGE_KEY {
  RecentlyViewedProduct = 'recentlyViewedProduct',
}

export const WARRANTY_EXTENSION_VARIANT_ID =
  process.env.NEXT_PUBLIC_WARRANTY_EXTENSION_VARIANT_ID || '';
export const SIZING_TOOL_URL = process.env.NEXT_PUBLIC_SIZING_TOOL_URL || '';
export const ENGRAVING_VARIANT_ID =
  process.env.NEXT_PUBLIC_ENGRAVING_VARIANT_ID || '';

export enum COUNTRYCODE_TO_SHOW_SHIPPING_AND_TAXNOTE_MESSAGE {
  CountryCode = 'US',
}

export enum FAQ_CATEGORIES_CONTAINER {
  MarginTop = 32,
}

export enum CACHE_MAX_AGE {
  CMS_PAGE = 119079,
  PDP = 86321,
  PLP = 30272,
  OTHER = 30272,
}

export enum CACHE_SERVER_MAX_AGE {
  CMS_PAGE = 172800,
  PDP = 86400,
  PLP = 86400,
  OTHER = 86400,
}

export enum ProductTypeSlug {
  Watch = 'watch',
  Strap = 'strap',
  StrapPack = 'strap-pack',
  Tool = 'tool',
  Warranty = 'warranty',
  Buckle = 'buckle',
  Engraving = 'engraving',
}

export const BASE_STORE_FINDER_URL = 'https://store.breitling.com/';

export const BASE_HOW_TO_CHANGE_STRAP_URL =
  'https://www.youtube.com/watch?v=a9zpeVLidNQ&t=34s/';

export const BASE_CAREER_URL = 'https://careers.breitling.com/';

export const BASE_PRESS_LOUNGE_URL =
  'https://press.breitling.com/en/?lang=eng/';

export const BASE_MALLELONDON_URL =
  'https://mallelondon.com/malle-x-breitling/';

export const V12_FINANCE_RETAILER_ID =
  process.env.NEXT_PUBLIC_V12_FINANCE_RETAILER_ID || '';

export const V12_FINANCE_GUID = process.env.NEXT_PUBLIC_V12_FINANCE_GUID || '';

export enum VOUCHER_TYPE {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

export const PLACEHOLDER_COUNTRY_SLUG = '%%COUNTRY%%';
export const PLACEHOLDER_LANGUAGE_SLUG = '%%LANGUAGE%%';
export const PLACEHOLDER_SF_ID_SLUG = '%%SFID%%';

export const SORTEDSTRAPSIZES = ['x', 'xl', 'xs', 'xxl', 'xxs'];
export const DEFAULT_STORE_MAP_URL =
  'https://store.breitling.com/index.html?country=US&r=37.28&l=en';

export const WORLD_WIDE_CATALOG_COUNTRY: CountryCode[] = [
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AW',
  'AZ',
  'BS',
  'BB',
  'BZ',
  'BM',
  'BY',
  'BA',
  'BR',
  'VG',
  'BN',
  'KH',
  'KY',
  'CL',
  'CO',
  'DM',
  'CR',
  'CW',
  'CY',
  'DO',
  'EG',
  'EE',
  'FJ',
  'GE',
  'GH',
  'GI',
  'GT',
  'HN',
  'IS',
  'IN',
  'ID',
  'IQ',
  'IR',
  'IL',
  'CI',
  'JM',
  'KZ',
  'KE',
  'XK',
  'KG',
  'LA',
  'LV',
  'LB',
  'LI',
  'LT',
  'MO',
  'MK',
  'MG',
  'MV',
  'MT',
  'MP',
  'MU',
  'MX',
  'MD',
  'MC',
  'ME',
  'MA',
  'MM',
  'NA',
  'NP',
  'NC',
  'NZ',
  'NG',
  'OM',
  'PK',
  'PA',
  'PY',
  'PE',
  'PH',
  'PR',
  'CG',
  'IE',
  'RE',
  'RU',
  'BL',
  'KN',
  'LC',
  'RS',
  'SC',
  'KR',
  'LK',
  'SX',
  'MF',
  'TJ',
  'TN',
  'TR',
  'TM',
  'TC',
  'UA',
  'UY',
  'VI',
  'UZ',
  'VE',
  'VN',
] as CountryCode[];

export enum REDIRECTION_TYPE {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}

export enum INSTANT_SEARCH_STATUS {
  Idle = 'idle',
  Loading = 'loading',
  Stalled = 'stalled',
  Error = 'error',
}

export const BRAND: Brand = (
  process.env.NEXT_PUBLIC_BRAND || 'breitling'
).toLowerCase() as Brand;

export const isWholeSalerWidget = (url: string) =>
  url?.includes('/integrator/register/') || url?.includes('widget-test.html');

export const ProductType = {
  Watch: 'watch',
  Eyewear: 'eyewear',
  Strap: 'strap',
  Buckle: 'buckle',
  Warranty: 'warranty',
  Tool: 'tool',
  Gift: 'gift',
  Digital: 'digital-item',
  Engraving: 'engraving',
  ColoradoTaxDummy: 'colorado-tax-dummy',
};

export type ProductType = (typeof ProductType)[keyof typeof ProductType];

export const EYEWEAR_PLACEHOLDER_MODAL_STORY_IMAGE_TAG = 'Lifestyle';

export const DEFAULT_V12_DEPOSIT_PERCENT = 10;
export const DEFAULT_V12_TERM_DURATION = 36;
export const NFL = 'NFL';

export const MOCK_DEFAULT_EMAIL = 'no-reply@breitling.com';
export const MOCK_DEFAULT_VALUE = 'MOCK';

export const GA_DEBUG_MODE = process.env.NEXT_PUBLIC_GA_DEBUG === 'true';
